import {Button, Col, Divider, Layout, Row} from "antd";
import {Helmet as Head} from "react-helmet-async";
import {useWindowSize} from "@components/window-size-provider";
import React, {useMemo} from "react";
import {Contain} from "@components/layout";
import {
    BackArrowIcon,
    CollectionManagementIcon,
    ContentIcon, CoverPriceLogoIcon,
    ImportCollectionIcon,
    ListsIcon,
    LongRightArrowIcon,
    MarketplaceIcon,
    NewReleasesIcon,
    PriceGuideIcon, StopHandIcon,
    TrendIcon
} from "@components/icons/HomePageIcons";
import {useNavigate} from "react-router-dom";
import useSubscriptionType from "@hooks/useSubscriptionType";
import sign_up_header_img from "@images/sign_up_header.png";
import restricted_area_bg_img from "./restricted_area_bg_img.jpg";
import {getWordpressUrl} from "@lib/wordpressVars";
import {scrollToThen} from "@lib/utils";

const RestrictedAccessListItem = ({IconClass, iconClass, title, description}) => {
    return (
        <div className={"modal-list-item"}>
            <IconClass className={iconClass} />
            <div className="modal-list-item-text">
                <b className="modal-list-item-title">{title}</b>
                {description}
            </div>
        </div>
    );
};

const RestrictedAccess = () => {
    const windowDimensions = useWindowSize();
    const navigate = useNavigate();
    const subscriptionType = useSubscriptionType();

    const showDesktopView = useMemo(() => {
        return windowDimensions.over(799);
    }, [windowDimensions]);

    const itemRowSpan = useMemo(() => {
        return showDesktopView ? 12 : 24;
    }, [showDesktopView]);

    const itemRowIconClass = useMemo(() => {
        return windowDimensions.over(1199) ? "greyBigCircleSmallIcon text-grey" : "greyCircleSmallIcon text-grey";
    }, [windowDimensions]);

    const arrowClass = useMemo(() => {
       return windowDimensions.over(1439) ? 'pad-left-50 pad-top-20' : 'pad-left-10 pad-top-20';
    }, [windowDimensions]);

    const goToMembership = () => scrollToThen({top: 0, behavior: 'instant'}, () => navigate('/plans/unlimited') )

    return (
        <Layout className={`partials restricted-access`} style={{
            background: `url(${getWordpressUrl(restricted_area_bg_img)}) repeat center center`,
        }}>
            <Head>
                <title>Membership Plans - CovrPrice</title>
            </Head>

            <Row className={arrowClass}>
                <Col>
                    <BackArrowIcon  onClick={() => navigate(-1)} />
                </Col>
            </Row>

            <Row className="top-box flex-align-center flex-vertical-center mar-top-40 mar-bot-10" style={{
                background: `url(${getWordpressUrl(sign_up_header_img)}) no-repeat center center`,
            }}>
                <div className="top-box-content">
                    <StopHandIcon className="top-box-icon"/>
                </div>
            </Row>

            <div className="restricted-container">

                <Divider className={"modal-divider"}/>

                <div className="text-center">
                    <p className={"modal-title"}>Members Only Content.</p>
                    <p className={"modal-subtitle mar-top-10 cursor-pointer"} onClick={goToMembership}>Get Full Access. Start your <b>FREE</b> trial!</p>
                </div>
                <Divider className={"modal-divider"}/>
                <Row gutter={[30, 30]} className={'mar-top-40 mar-bot-40'}>
                    <Col span={itemRowSpan}>
                        <RestrictedAccessListItem
                            IconClass={PriceGuideIcon}
                            iconClass={itemRowIconClass}
                            title={"Price Guides & Values"}
                            description={"See real and aftermarket sales for raw and graded comics."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <RestrictedAccessListItem
                            IconClass={NewReleasesIcon}
                            iconClass={itemRowIconClass}
                            title={"New Releases"}
                            description={"Find comics coming out each week including hard to find exclusives."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <RestrictedAccessListItem
                            IconClass={CollectionManagementIcon}
                            iconClass={itemRowIconClass}
                            title={"Collection Management"}
                            description={"Track and analyze your entire collection in one place."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <RestrictedAccessListItem
                            IconClass={ContentIcon}
                            iconClass={itemRowIconClass}
                            title={"Comic Content & News"}
                            description={"Market reports, convention hit-lists, New release finds, Top 10s, FOC's and more."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <RestrictedAccessListItem
                            IconClass={TrendIcon}
                            iconClass={itemRowIconClass}
                            title={"Trend Tracking"}
                            description={"View Hot Comics, Daily Shakers, Weekly Movers and curated lists."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <RestrictedAccessListItem
                            IconClass={ListsIcon}
                            iconClass={itemRowIconClass}
                            title={"Key Info & Lists"}
                            description={"Get the details on what comics are key and why."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <RestrictedAccessListItem
                            IconClass={ImportCollectionIcon}
                            iconClass={itemRowIconClass}
                            title={"Import Existing Collections"}
                            description={"Dont start from scratch, Send us your existing (CSV file)"}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <RestrictedAccessListItem
                            IconClass={MarketplaceIcon}
                            iconClass={itemRowIconClass}
                            title={"Aggregated Marketplace"}
                            description={"Search for comics across your favorite online stores."}
                        />
                    </Col>
                </Row>

                <Divider className={"modal-divider-transparent"}/>

                {subscriptionType.isLoggedIn ? (
                    <div className="action-bar">
                        <div className="action-bar-text">Upgrade Your Membership</div>
                        <div className="action-bar-arrow">
                            <LongRightArrowIcon />
                        </div>
                        <Button className={"action-bar-button"} onClick={goToMembership}>Upgrade</Button>
                    </div>
                ) : (
                    <>
                        <div className="action-bar">
                            <div className="action-bar-text">Start your FREE&nbsp;trial!</div>
                            <div className="action-bar-arrow">
                                <LongRightArrowIcon />
                            </div>
                            <Button className={"action-bar-button"} onClick={() => navigate('/register')}>Start Here</Button>
                        </div>
                        <div className="action-bar-footer">
                            <div className="pad-10 font-16">Already a member? <a href={"/login"}>Sign In</a></div>
                        </div>
                    </>
                )}
                <Divider className={"modal-divider-transparent"}/>

            </div>

        </Layout>
    );
};

export default RestrictedAccess;
